import React, { useState, useEffect, useCallback } from "react";
import { Box, TextField, Typography, Modal, Grid } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Button } from "@deere/fuel-react";
import { Close, Send } from "@mui/icons-material";
import ToastMessageFunction from "../../common/toast-message-function";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

// Define the editable fields
const editableFields = [
  "serviceRenderDate",
  "material",
  "targetQty",
  "amount", //amount
  "currencydisplay",
  "itemTextDisplay",
  "taxDepartureCountry",
  "dealerNumber",
  "profitCenter",
];

// Define the compulsory fields
const compulsoryFields = [
"serviceRenderDate",
  "material",
  "targetQty",
  "amount", //amount
  "currencydisplay",
  "dealerNumber",
];

const flattenTransaction = (transaction) => {
  let flattenedData = { ...transaction };

  if (transaction && transaction.debitMemoRequestItem && transaction.debitMemoRequestItem.length > 0) {
    flattenedData = { ...transaction, ...transaction.debitMemoRequestItem[0] };
    delete flattenedData.debitMemoRequestItem;
  } else if (transaction && transaction.creditMemoRequestItem && transaction.creditMemoRequestItem.length > 0) {
    flattenedData = { ...transaction, ...transaction.creditMemoRequestItem[0] };
    delete flattenedData.creditMemoRequestItem;
  }

  return flattenedData;
};

const EditTransactionModal = ({
  open,
  onClose,
  editingTransaction,
  handleSaveClick,
}) => {

  const [editedTransaction, setEditedTransaction] =
    useState(flattenTransaction(editingTransaction));
  const toastManager = ToastMessageFunction();

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(validateFields(editedTransaction));
  }, [editedTransaction]);

  useEffect(() => {
    if (editingTransaction) {
      editingTransaction = flattenTransaction(editingTransaction);
      setEditedTransaction({
        ...editingTransaction,
        billingDate: dayjs(editingTransaction.billingDate).isValid()
          ? dayjs(editingTransaction.billingDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"), // Fallback to default date if invalid
        serviceRenderDate: dayjs(editingTransaction.serviceRenderDate).isValid()
          ? dayjs(editingTransaction.serviceRenderDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"), // Fallback to default date if invalid
      });
    }
  }, [editingTransaction]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const [field, itemIndex, textIndex, condIndex] = name.split("-");

    if (itemIndex !== undefined) {
      if (editedTransaction.debitMemoRequestItem) {
        const updatedItems = [...editedTransaction.debitMemoRequestItem];
        if (textIndex !== undefined) {
          const updatedItemTexts = [...updatedItems[itemIndex].itemText];
          updatedItemTexts[textIndex] = value;
          updatedItems[itemIndex].itemText = updatedItemTexts;
        } else if (condIndex !== undefined) {
          updatedItems[itemIndex].debitMemoRequestItemConditions[condIndex][
            field
          ] = value;
        } else {
          updatedItems[itemIndex][field] = value;
        }
        setEditedTransaction({
          ...editedTransaction,
          debitMemoRequestItem: updatedItems,
        });
      }
      if (editedTransaction.creditMemoRequestItem) {
        const updatedItems = [...editedTransaction.creditMemoRequestItem];
        if (textIndex !== undefined) {
          const updatedItemTexts = [...updatedItems[itemIndex].itemText];
          updatedItemTexts[textIndex] = value;
          updatedItems[itemIndex].itemText = updatedItemTexts;
        } else if (condIndex !== undefined) {
          updatedItems[itemIndex].creditMemoRequestItem[condIndex][field] =
            value;
        } else {
          updatedItems[itemIndex][field] = value;
        }
        setEditedTransaction({
          ...editedTransaction,
          creditMemoRequestItem: updatedItems,
        });
      }
    } else {
      setEditedTransaction({
        ...editedTransaction,
        [field]: value,
      });
    }
  };

  const onSaveClick = () => {
    if (!validateFields(editedTransaction)) {
      showToast("Please fill all compulsory fields", "error");
    }
    handleSaveClick(editedTransaction);
  };

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const onChangeDatePicker = (date, key) => {
    const currentDate = dayjs();
    const lastDateOfCurrentMonth = currentDate.endOf('month');
    const dateOneYearAgo = currentDate.subtract(1, 'year');

    if (!date || !date.isValid()) {
      showToast("Date is invalid", "error");
      setIsFormValid(false);
    } else if (date.isAfter(lastDateOfCurrentMonth) && date.isBefore(dateOneYearAgo)) {
      showToast("Date must be within a year and this month", "error");
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
      const formattedDate = dayjs(date).format("YYYY-MM-DD").toString();
      handleInputChange({ target: { name: key, value: formattedDate } });
    }
  };

  const validateFields = (formData) => {
    let isValid = true;
    if (!formData || Object.keys(formData).length === 0) {
      return true;
    }
    compulsoryFields.forEach((field) => {
      if (formData && !formData[field]) {
        isValid = false;
        if (field === "currencydisplay") {
          showToast(`currency is required`, "error");
        } else if (field === "itemTextDisplay") {
          showToast(`ItemText is required`, "error");
        } else {
          showToast(`${field} is required`, "error");
        }
      }
    });

    if (formData && ( isNaN(Number(formData.amount)) || Number(formData.amount) <= 0)) {
      isValid = false;
      showToast("Amount must be an decimal", "error");
    }

    return isValid;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{ ...modalStyle, width: 800, height: "87vh", overflowY: "auto" }}
      >
        {toastManager.toast}
        <Typography id="modal-modal-title" variant="h4" component="h2">
          <strong>Edit Transaction</strong>
        </Typography>
        {editedTransaction && (
          <Box component="form" sx={{ mt: 5 }}>
            <Grid container spacing={2}>
              {Object.entries(editedTransaction).map(
                ([key, value]) =>
                  editableFields.includes(key) && (
                    <Grid item xs={12} md={key === "itemTextDisplay" ? 12 : 6} key={key}>
                      { key === "serviceRenderDate" ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            fullWidth
                            label={`${key.replace(/([A-Z])/g, " $1")}${
                              compulsoryFields.includes(key) ? " *" : ""
                            }`}
                            value={dayjs(value)}
                            onChange={(date) => onChangeDatePicker(date, key)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                inputProps={{ ...params.inputProps }}
                              />
                            )}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <TextField
                          //fullWidth
                          name={
                            key === "dealerNumber" &&
                            editedTransaction.isSapNumberUploaded &&
                            editedTransaction.sapNumber &&
                            editedTransaction.source === "UPLOAD"
                              ? "sapNumber"
                              : key
                          }
                          value={
                            key === "dealerNumber" &&
                            editedTransaction.isSapNumberUploaded &&
                            editedTransaction.sapNumber &&
                            editedTransaction.source === "UPLOAD"
                              ? editedTransaction.sapNumber || ""
                              : value || ""
                          }
                          onChange={handleInputChange}
                          multiline
                          label={
                            key === "itemTextDisplay"
                              ? "item Text"
                              : key === "currencydisplay"
                              ? "currency *"
                              : key === "dealerNumber"
                              ? `Legacy Dealer Account/SAP Account${compulsoryFields.includes(key) ? " *" : ""}`
                              : `${key.replace(/([A-Z])/g, " $1")}${
                                  compulsoryFields.includes(key) ? " *" : ""
                                }`
                          }
                          sx={{
                           width: "99%"
                          }}
                        />
                      )}
                    </Grid>
                  )
              )}
            </Grid>
            <Box sx={{ marginTop: 2, textAlign: "right" }}>
              <Button
                variant="primary"
                color="primary"
                onClick={onSaveClick}
                endIcon={<Send />}
                disabled={!isFormValid}
              >
                Validate
              </Button>
              <Button
                variant="secondary"
                sx={{ marginLeft: 2 }}
                onClick={onClose}
                endIcon={<Close />}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default EditTransactionModal;
